import { reactive, readonly } from 'vue';
// eslint-disable-next-line import/no-cycle
import { UserState } from '@/composables/User';
// eslint-disable-next-line import/no-cycle
import { securedNestGet, securedNestPost } from '@/composables/nestApi';

/**
 * Statuts renvoyés par GET /migration-v5/:idShop/status (backend V5 /
 * client_workflow sur feature/newV5). Reflète la clé
 * `platform_migration_v5_pending` dans `shops_configuration` :
 *   in_progress '1' · ready_to_migrate_data '2' (module connecté) ·
 *   data_migration_in_progress '2.5' (ZF2 migre) · data_migrated '3' ·
 *   finalized '4'.
 * Les '2'/'2.5' sont des états ACTIFS du tunnel : le backend les renvoie,
 * il faut donc les router comme les autres (cf. shouldRedirectToV5).
 */
export type MigrationV5Status =
  | 'none'
  | 'invited'
  | 'in_progress'
  | 'ready_to_migrate_data'
  | 'data_migration_in_progress'
  | 'data_migrated'
  | 'finalized';

const V5_FRONT_URL = 'https://my.new.shopimind.com';
const SNOOZE_KEY = 'spm:migration-v5:modal-snoozed-until';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

/**
 * État partagé entre App.vue (qui décide d'ouvrir la modal) et le
 * header (qui affiche le bouton "Migrer" conditionnellement et permet
 * de réouvrir la modal).
 */
type MigrationV5UiState = {
  status: MigrationV5Status;
  modalOpen: boolean;
};

const migrationV5State = reactive<MigrationV5UiState>({
  status: 'none',
  modalOpen: false,
});

export const MigrationV5State = readonly(migrationV5State);

export function openMigrationV5Modal(): void {
  migrationV5State.modalOpen = true;
}

export function closeMigrationV5Modal(): void {
  migrationV5State.modalOpen = false;
}

export async function fetchMigrationV5Status(): Promise<MigrationV5Status> {
  const id = UserState.activeShop?.id;
  if (!id) return 'none';
  const res = await securedNestGet('v5', `/migration-v5/${id}/status`);
  const next = (res?.status as MigrationV5Status) ?? 'none';
  migrationV5State.status = next;
  return next;
}

export async function startMigrationV5(): Promise<void> {
  const id = UserState.activeShop?.id;
  if (!id) return;
  await securedNestPost('v5', `/migration-v5/${id}/start`, {}, {});
}

/**
 * Statuts pour lesquels le shop doit être PARQUÉ sur le wizard V5
 * `/migration-v5/` — migration ACTIVE, le shop n'est pas encore un shop
 * plateforme V5. On couvre TOUT le tunnel actif :
 *   in_progress · ready_to_migrate_data · data_migration_in_progress ·
 *   data_migrated.
 *
 * Pourquoi '2'/'2.5' sont indispensables : un nouvel onglet ouvert sur
 * `my.new` pendant ces états tombe sur le dashboard (path NON exempté) →
 * `redirectIfV4Shop` (V5, cookie encore `v4`) le rebounce vers V4. C'est ce
 * guard qui doit alors le renvoyer sur `/migration-v5/` (exempté) ; sans ces
 * deux statuts il `next()` et l'utilisateur reste bloqué sur V4.
 *
 * ⚠️ `finalized` est volontairement EXCLU (≠ du set côté nest, où '4'→v5 est
 * le VERDICT plateforme). Ici le sens est « parquer sur le WIZARD » : or le
 * wizard en finalized fait `redirectNow()` → `my.new/` (path NON exempté) →
 * rebounce → boucle. Un shop finalisé est routé par le cookie
 * `spm_platform_version === 'v5'` (1er `beforeEach`), vers le DASHBOARD.
 */
export function shouldRedirectToV5(status: MigrationV5Status): boolean {
  return (
    status === 'in_progress'
    || status === 'ready_to_migrate_data'
    || status === 'data_migration_in_progress'
    || status === 'data_migrated'
    || status === 'finalized'
  );
}

/**
 * Hard redirect vers la page de migration V5. On cible `/migration-v5/`
 * explicitement (PAS `window.location.pathname`) — autrement V5 reçoit
 * l'utilisateur sur un path V4 quelconque qui n'est pas dans
 * `V4_REDIRECT_EXEMPT_PREFIXES`, et `redirectIfV4Shop` bounce vers V4 →
 * boucle infinie my <-> my.new. Le path `/migration-v5/` est exempt
 * côté V5 (cf. src/composables/shopVersion.ts feature/newV5).
 */
export function redirectToV5(): void {
  window.location.href = `${V5_FRONT_URL}/migration-v5/`;
}

export function isModalSnoozed(): boolean {
  const raw = localStorage.getItem(SNOOZE_KEY);
  if (!raw) return false;
  const until = Number(raw);
  if (!Number.isFinite(until)) return false;
  return Date.now() < until;
}

export function snoozeModal(): void {
  localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
}

export function clearSnooze(): void {
  localStorage.removeItem(SNOOZE_KEY);
}
